import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Copy,
  Eye,
  EyeOff,
  Globe,
  Key,
  KeyRound,
  Lock,
  Mail,
  Pencil,
  Plus,
  Server,
  Shield,
  StickyNote,
  Trash2,
} from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';

import { ApiError, apiFetch } from '@/lib/api';

type CredentialKind =
  | 'PASSWORD'
  | 'SSH'
  | 'RDP'
  | 'SMTP'
  | 'API_KEY'
  | 'DB'
  | 'URL_LOGIN'
  | 'NOTE'
  | 'OTHER';

interface Credential {
  id: string;
  clientId: string;
  label: string;
  kind: CredentialKind;
  url: string | null;
  username: string | null;
  notes: string | null;
  expiresAt: string | null;
  lastRevealedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const KIND_LABEL: Record<CredentialKind, string> = {
  PASSWORD: 'Senha',
  SSH: 'SSH',
  RDP: 'RDP',
  SMTP: 'SMTP',
  API_KEY: 'API Key',
  DB: 'Banco de Dados',
  URL_LOGIN: 'Painel (URL+login)',
  NOTE: 'Nota',
  OTHER: 'Outros',
};

const KIND_ICON: Record<CredentialKind, typeof Key> = {
  PASSWORD: KeyRound,
  SSH: Server,
  RDP: Server,
  SMTP: Mail,
  API_KEY: Key,
  DB: Server,
  URL_LOGIN: Globe,
  NOTE: StickyNote,
  OTHER: Shield,
};

export function ClientCredentialsTab({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Credential | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const list = useQuery({
    queryKey: ['credentials', clientId],
    queryFn: () => apiFetch<Credential[]>(`/clients/${clientId}/credentials`),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/clients/${clientId}/credentials/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['credentials', clientId] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground flex items-center gap-2">
          <Lock className="h-4 w-4 text-primary" />
          Senhas, tokens, chaves de API e acessos. Valores cifrados AES-256-GCM.
        </p>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> Nova credencial
        </button>
      </div>

      {list.isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
      {list.data && list.data.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-border p-12 text-center">
          <Lock className="h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            Nenhuma credencial cadastrada para este cliente.
          </p>
        </div>
      )}
      {list.data && list.data.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {list.data.map((c) => (
            <CredentialCard
              key={c.id}
              cred={c}
              clientId={clientId}
              onEdit={() => setEditing(c)}
              onDelete={() => {
                if (confirm(`Excluir credencial "${c.label}"?`)) remove.mutate(c.id);
              }}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CredentialFormDialog
          clientId={clientId}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['credentials', clientId] });
          }}
        />
      )}
      {editing && (
        <CredentialFormDialog
          clientId={clientId}
          credential={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['credentials', clientId] });
          }}
        />
      )}
    </div>
  );
}

function CredentialCard({
  cred,
  clientId,
  onEdit,
  onDelete,
}: {
  cred: Credential;
  clientId: string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const Icon = KIND_ICON[cred.kind];
  const [revealed, setRevealed] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);

  async function toggleReveal() {
    if (revealed !== null) {
      setRevealed(null);
      return;
    }
    setRevealing(true);
    try {
      const res = await apiFetch<{ value: string }>(
        `/clients/${clientId}/credentials/${cred.id}/reveal`,
        { method: 'POST' },
      );
      setRevealed(res.value);
      setTimeout(() => setRevealed(null), 60_000);
    } catch (err) {
      alert(
        err instanceof ApiError
          ? `Erro: ${err.body}`
          : err instanceof Error
            ? err.message
            : 'Falha ao revelar',
      );
    } finally {
      setRevealing(false);
    }
  }

  function copyToClipboard() {
    if (revealed) navigator.clipboard.writeText(revealed).catch(() => undefined);
  }

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="rounded-md bg-primary/10 p-2 text-primary">
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium">{cred.label}</div>
            <div className="text-xs text-muted-foreground">
              {KIND_LABEL[cred.kind]}
              {cred.username && (
                <>
                  {' • '}
                  <span className="font-mono">{cred.username}</span>
                </>
              )}
            </div>
            {cred.url && (
              <div className="text-xs text-muted-foreground truncate font-mono mt-1">
                {cred.url}
              </div>
            )}
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 rounded bg-muted px-2 py-1 text-xs font-mono">
                {revealed ?? '••••••••••••'}
              </code>
              <button
                type="button"
                onClick={toggleReveal}
                disabled={revealing}
                data-dica={revealed ? 'Ocultar' : 'Mostrar'}
                className="rounded-md border border-input p-1.5 text-muted-foreground hover:bg-accent disabled:opacity-50"
              >
                {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </button>
              {revealed && (
                <button
                  type="button"
                  onClick={copyToClipboard}
                  data-dica="Copiar"
                  className="rounded-md border border-input p-1.5 text-muted-foreground hover:bg-accent"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {cred.notes && (
              <p className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">{cred.notes}</p>
            )}
            {cred.lastRevealedAt && (
              <p className="mt-1 text-[10px] text-muted-foreground/70">
                Último acesso: {new Date(cred.lastRevealedAt).toLocaleString('pt-BR')}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={onEdit}
            data-dica="Editar"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            data-dica="Excluir"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function CredentialFormDialog({
  clientId,
  credential,
  onClose,
  onSaved,
}: {
  clientId: string;
  credential?: Credential;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!credential;
  const [form, setForm] = useState({
    label: credential?.label ?? '',
    kind: (credential?.kind ?? 'PASSWORD') as CredentialKind,
    url: credential?.url ?? '',
    username: credential?.username ?? '',
    value: '',
    notes: credential?.notes ?? '',
    expiresAt: credential?.expiresAt ? credential.expiresAt.slice(0, 10) : '',
  });
  const [showValue, setShowValue] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (credential) {
      setForm({
        label: credential.label,
        kind: credential.kind,
        url: credential.url ?? '',
        username: credential.username ?? '',
        value: '',
        notes: credential.notes ?? '',
        expiresAt: credential.expiresAt ? credential.expiresAt.slice(0, 10) : '',
      });
    }
  }, [credential]);

  const save = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        label: form.label,
        kind: form.kind,
        url: form.url || undefined,
        username: form.username || undefined,
        value: form.value || undefined,
        notes: form.notes || undefined,
        expiresAt: form.expiresAt || undefined,
      });
      return isEdit
        ? apiFetch(`/clients/${clientId}/credentials/${credential!.id}`, {
            method: 'PATCH',
            body,
          })
        : apiFetch(`/clients/${clientId}/credentials`, { method: 'POST', body });
    },
    onSuccess: onSaved,
    onError: (err) =>
      setError(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-medium">
            {isEdit ? `Editar — ${credential!.label}` : 'Nova credencial'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <Field label="Identificação (label) *">
            <input
              required
              className="input"
              placeholder="ex.: Servidor produção"
              value={form.label}
              onChange={(e) => setForm((s) => ({ ...s, label: e.target.value }))}
            />
          </Field>
          <Field label="Tipo *">
            <select
              required
              className="input"
              value={form.kind}
              onChange={(e) => setForm((s) => ({ ...s, kind: e.target.value as CredentialKind }))}
            >
              {(Object.keys(KIND_LABEL) as CredentialKind[]).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="URL / Host">
              <input
                className="input font-mono text-xs"
                placeholder="ssh://x.cliente.com:22"
                value={form.url}
                onChange={(e) => setForm((s) => ({ ...s, url: e.target.value }))}
              />
            </Field>
            <Field label="Usuário">
              <input
                className="input font-mono"
                value={form.username}
                onChange={(e) => setForm((s) => ({ ...s, username: e.target.value }))}
              />
            </Field>
          </div>
          <Field label={isEdit ? 'Senha/Valor (vazio = manter atual)' : 'Senha/Valor *'}>
            <div className="flex gap-2">
              <input
                required={!isEdit}
                type={showValue ? 'text' : 'password'}
                className="input font-mono"
                value={form.value}
                onChange={(e) => setForm((s) => ({ ...s, value: e.target.value }))}
              />
              <button
                type="button"
                onClick={() => setShowValue((s) => !s)}
                className="rounded-md border border-input px-3 text-muted-foreground hover:bg-accent"
              >
                {showValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>
          <Field label="Validade (opcional)">
            <input
              type="date"
              className="input"
              value={form.expiresAt}
              onChange={(e) => setForm((s) => ({ ...s, expiresAt: e.target.value }))}
            />
          </Field>
          <Field label="Notas (contexto — NÃO cifrado)">
            <textarea
              rows={3}
              className="input"
              placeholder="Ex.: porta 22, fingerprint X, contato do TI: João"
              value={form.notes}
              onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
            />
          </Field>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {save.isPending ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Criar'}
            </button>
          </div>
        </form>

        <style>{`
          .input {
            width: 100%;
            border: 1px solid hsl(var(--input));
            background: hsl(var(--background));
            padding: 0.5rem 0.75rem;
            border-radius: 0.375rem;
            font-size: 0.875rem;
          }
          .input:focus { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring) / 0.4); }
        `}</style>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}
